import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  HomeIcon,
  UserIcon,
  CreditCardIcon,
  BanknotesIcon,
  ChatBubbleLeftRightIcon,
  ChartBarIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  UsersIcon,
  CurrencyDollarIcon,
  AdjustmentsHorizontalIcon,
  BellIcon,
  InboxIcon,
  KeyIcon,
  SignalIcon,
} from '@heroicons/react/24/outline';
import Logo from './Logo';

const Layout = ({ children }) => {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
    { name: 'Profile', href: '/profile', icon: UserIcon },
    { name: 'Subscriptions', href: '/subscriptions', icon: CreditCardIcon },
    { name: 'Payments', href: '/payments', icon: BanknotesIcon },
    { name: 'Chatbot', href: '/typebot', icon: ChatBubbleLeftRightIcon },
    { name: 'Inbox', href: '/inbox', icon: InboxIcon },
    { name: 'Analytics', href: '/analytics', icon: ChartBarIcon },
    ...(user?.role === 'owner' || user?.role === 'admin' ? [
      { name: 'Team', href: '/team', icon: UsersIcon },
      { name: 'API Keys', href: '/api-keys', icon: KeyIcon },
    ] : []),
  ];

  const adminNavigation = [
    { name: 'Admin Dashboard', href: '/admin', icon: ChartBarIcon },
    { name: 'Manage Users', href: '/admin/users', icon: UsersIcon },
    { name: 'View Subscriptions', href: '/admin/subscriptions', icon: CurrencyDollarIcon },
    { name: 'Session Manager', href: '/admin/sessions', icon: SignalIcon },
    { name: 'System Settings', href: '/admin/settings', icon: AdjustmentsHorizontalIcon },
  ];

  const isActive = (href) => location.pathname === href;

  const NavItem = ({ item, onClick }) => (
    <Link
      to={item.href}
      onClick={onClick}
      className={`group flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
        isActive(item.href)
          ? 'bg-primary-500 text-white shadow-orange'
          : 'text-neutral-600 hover:bg-primary-50 hover:text-primary-600'
      }`}
    >
      <item.icon className={`h-5 w-5 flex-shrink-0 ${
        isActive(item.href) ? 'text-white' : 'text-neutral-400 group-hover:text-primary-500'
      }`} />
      <span>{item.name}</span>
      {isActive(item.href) && (
        <div className="ml-auto w-1.5 h-1.5 rounded-full bg-white" />
      )}
    </Link>
  );

  const UserProfile = ({ compact = false }) => (
    <div className={`flex items-center ${compact ? 'gap-2' : 'gap-3'}`}>
      <div className={`${compact ? 'w-8 h-8' : 'w-10 h-10'} rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shadow-sm`}>
        <span className={`${compact ? 'text-xs' : 'text-sm'} font-semibold text-white`}>
          {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
        </span>
      </div>
      {!compact && (
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-neutral-800 truncate">
            {user?.firstName} {user?.lastName}
          </p>
          <p className="text-xs text-neutral-500 truncate">{user?.email}</p>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-neutral-100">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-neutral-900/60 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-72 transform transition-transform duration-300 ease-out lg:hidden ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex h-full flex-col bg-white shadow-xl">
          {/* Mobile Sidebar Header */}
          <div className="flex h-16 items-center justify-between px-4 border-b border-neutral-200">
            <Logo className="h-8" showText={true} variant="dark" />
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-2 rounded-lg text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 transition-colors"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Mobile Navigation */}
          <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
            {navigation.map((item) => (
              <NavItem key={item.name} item={item} onClick={() => setSidebarOpen(false)} />
            ))}
            
            {isAdmin && (
              <>
                <div className="pt-4 pb-2">
                  <div className="flex items-center gap-2 px-3">
                    <div className="h-px flex-1 bg-neutral-200" />
                    <span className="text-xs font-medium text-neutral-400 uppercase tracking-wider">Admin</span>
                    <div className="h-px flex-1 bg-neutral-200" />
                  </div>
                </div>
                {adminNavigation.map((item) => (
                  <NavItem key={item.name} item={item} onClick={() => setSidebarOpen(false)} />
                ))}
              </>
            )}
          </nav>

          {/* Mobile User Section */}
          <div className="border-t border-neutral-200 p-4 space-y-3">
            <UserProfile />
            <button
              onClick={handleLogout}
              className="flex w-full items-center gap-3 px-3 py-2.5 text-sm font-medium text-neutral-600 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5" />
              <span>Sign out</span>
            </button>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-white border-r border-neutral-200">
          {/* Desktop Sidebar Header */}
          <div className="flex h-16 items-center px-5 border-b border-neutral-200">
            <Logo className="h-8" showText={true} variant="dark" />
          </div>

          {/* Desktop Navigation */}
          <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
            {navigation.map((item) => (
              <NavItem key={item.name} item={item} />
            ))}
            
            {isAdmin && (
              <>
                <div className="pt-6 pb-2">
                  <div className="flex items-center gap-2 px-3">
                    <div className="h-px flex-1 bg-neutral-200" />
                    <span className="text-xs font-medium text-neutral-400 uppercase tracking-wider">Admin</span>
                    <div className="h-px flex-1 bg-neutral-200" />
                  </div>
                </div>
                {adminNavigation.map((item) => (
                  <NavItem key={item.name} item={item} />
                ))}
              </>
            )}
          </nav>

          {/* Desktop User Section */}
          <div className="border-t border-neutral-200 p-4 space-y-3">
            <UserProfile />
            <button
              onClick={handleLogout}
              className="flex w-full items-center gap-3 px-3 py-2.5 text-sm font-medium text-neutral-600 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5" />
              <span>Sign out</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64 flex flex-col min-h-screen">
        {/* Top header bar */}
        <header className="sticky top-0 z-30 bg-white border-b border-neutral-200 shadow-sm">
          <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
            {/* Mobile menu button */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 -ml-2 rounded-lg text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 transition-colors"
            >
              <Bars3Icon className="h-6 w-6" />
            </button>

            {/* Mobile Logo (centered) */}
            <div className="lg:hidden">
              <Logo className="h-7" showText={false} variant="dark" />
            </div>

            {/* Page title area - desktop */}
            <div className="hidden lg:block">
              <h1 className="text-lg font-semibold text-neutral-800">
                {navigation.find(n => isActive(n.href))?.name || 
                 adminNavigation.find(n => isActive(n.href))?.name || 
                 'Dashboard'}
              </h1>
            </div>

            {/* Right side actions */}
            <div className="flex items-center gap-3">
              {/* Notifications */}
              <button className="relative p-2 rounded-lg text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 transition-colors">
                <BellIcon className="h-5 w-5" />
                <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-primary-500 rounded-full" />
              </button>

              {/* User avatar - desktop */}
              <div className="hidden lg:block">
                <UserProfile compact />
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 py-6 lg:py-8">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {children}
          </div>
        </main>

        {/* Footer - always at bottom */}
        <footer className="border-t border-neutral-200 bg-white py-4">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <p className="text-center text-xs text-neutral-400">
              © 2025 Chatza Client Manager. Powered by <span className="text-primary-500 font-medium">[ chatza ]</span>
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Layout;
